// src/pages/safety/SafetyScoreAnalysis.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, AlertTriangle, CheckCircle, Info, TrendingUp, Activity, Heart, Clock, Target } from 'lucide-react';
import { useSafety } from '../../contexts/SafetyContext.js';
import { useAuth } from '../../contexts/AuthContext.js';
import { useProgress } from '../../contexts/ProgressContext.js';

const SafetyScoreAnalysis = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getSafetyScore, getPainHistory, getExerciseHistory, getFormChecks, getFatigueLevel, contacts, formChecks } = useSafety();
  const { workoutHistory } = useProgress();
  
  const [safetyScore, setSafetyScore] = useState(0);
  const [scoreBreakdown, setScoreBreakdown] = useState({});
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const calculateDetailedScore = async () => {
      try {
        console.log('Calculating safety score...', { contacts, formChecks, getFatigueLevel });
        
        // Use the same calculation logic as SafetyContext
        const baseScore = 75;
        
        // Get emergency contacts count
        const contactAdjustment = Math.min((contacts || []).length * 5, 10);
        
        // Get form checks count
        const formCheckCount = formChecks ? Object.values(formChecks).flat().length : 0;
        const formAdjustment = Math.min(formCheckCount, 10);
        
        // Get fatigue level - handle case where getFatigueLevel might not exist
        let fatigueLevel = 5; // Default
        try {
          if (getFatigueLevel && typeof getFatigueLevel === 'function') {
            fatigueLevel = getFatigueLevel();
          }
        } catch (error) {
          console.warn('Error getting fatigue level:', error);
          fatigueLevel = 5; // Use default
        }
        
        const fatigueAdjustment = (10 - fatigueLevel) * 0.5;
        
        // Calculate total score using same logic as SafetyContext
        const totalScore = Math.min(100, baseScore + contactAdjustment + formAdjustment + fatigueAdjustment);
        const roundedScore = Math.round(totalScore);
        
        // Create detailed breakdown for display
        const breakdown = {
          baseScore: baseScore,
          emergencyContacts: contactAdjustment,
          formChecks: formAdjustment,
          fatigueManagement: fatigueAdjustment,
          total: roundedScore
        };
        
        console.log('Safety score calculated:', { roundedScore, breakdown });
        
        setSafetyScore(roundedScore);
        setScoreBreakdown(breakdown);
        setRecommendations(generateRecommendations(breakdown, roundedScore));
        
      } catch (error) {
        console.error('Error calculating safety score:', error);
        // Set default values on error
        setSafetyScore(75);
        setScoreBreakdown({
          baseScore: 75,
          emergencyContacts: 0,
          formChecks: 0,
          fatigueManagement: 0,
          total: 75
        });
        setRecommendations([]);
      } finally {
        setLoading(false);
      }
    };

    calculateDetailedScore();
  }, [contacts, formChecks, getFatigueLevel]);

  const calculatePainScore = (painHistory) => {
    if (!painHistory || painHistory.length === 0) return 100;
    
    const recentPain = painHistory.filter(p => 
      new Date(p.date) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    );
    
    if (recentPain.length === 0) return 100;
    
    const avgPainLevel = recentPain.reduce((sum, p) => sum + p.level, 0) / recentPain.length;
    return Math.max(0, 100 - (avgPainLevel * 20));
  };

  const calculateFormScore = (formChecks) => {
    if (!formChecks || formChecks.length === 0) return 50;
    
    const recentChecks = formChecks.filter(f => 
      new Date(f.date) > new Date(Date.now() - 14 * 24 * 60 * 60 * 1000)
    );
    
    if (recentChecks.length === 0) return 50;
    
    const avgFormScore = recentChecks.reduce((sum, f) => sum + f.score, 0) / recentChecks.length;
    return Math.round(avgFormScore);
  };

  const calculateFatigueScore = (fatigueLevel) => {
    if (fatigueLevel === undefined) return 50;
    return Math.max(0, 100 - (fatigueLevel * 25));
  };

  const calculateConsistencyScore = (exerciseHistory) => {
    if (!exerciseHistory || exerciseHistory.length === 0) return 0;
    
    const last30Days = exerciseHistory.filter(e => 
      new Date(e.date) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    );
    
    const consistencyRate = last30Days.length / 30;
    return Math.min(100, consistencyRate * 100);
  };

  const calculateRecoveryScore = (workoutHistory) => {
    if (!workoutHistory || workoutHistory.length === 0) return 50;
    
    const recentWorkouts = workoutHistory.filter(w => 
      new Date(w.completedAt) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    );
    
    if (recentWorkouts.length === 0) return 50;
    
    // Simple recovery score based on workout frequency
    const daysBetweenWorkouts = recentWorkouts.length > 1 ? 
      (new Date(recentWorkouts[0].completedAt) - new Date(recentWorkouts[recentWorkouts.length - 1].completedAt)) / (1000 * 60 * 60 * 24) / recentWorkouts.length : 1;
    
    return Math.min(100, Math.max(0, 100 - (daysBetweenWorkouts - 1) * 20));
  };

  const generateRecommendations = (breakdown, score) => {
    const recs = [];
    
    // Emergency contacts recommendations
    if ((contacts || []).length < 2) {
      recs.push({
        category: 'Emergency Contacts',
        priority: 'high',
        message: 'Add at least 2 emergency contacts for better safety coverage.',
        icon: AlertTriangle
      });
    }
    
    // Form checks recommendations
    const formCheckCount = formChecks ? Object.values(formChecks).flat().length : 0;
    if (formCheckCount < 5) {
      recs.push({
        category: 'Form Checks',
        priority: 'medium',
        message: 'Complete more form checks to improve your safety score and technique. Use the AI-powered form analysis features during workouts.',
        icon: Target
      });
    }
    
    // Fatigue management recommendations
    const fatigueLevel = getFatigueLevel ? getFatigueLevel() : 5;
    if (fatigueLevel > 7) {
      recs.push({
        category: 'Fatigue Management',
        priority: 'medium',
        message: 'Your fatigue level is high. Consider taking more rest and reducing workout intensity.',
        icon: Clock
      });
    }
    
    // Overall score recommendations
    if (score >= 90) {
      recs.push({
        category: 'Overall',
        priority: 'low',
        message: 'Excellent safety practices! Keep up the good work and maintain your current habits.',
        icon: CheckCircle
      });
    } else if (score < 70) {
      recs.push({
        category: 'Overall',
        priority: 'high',
        message: 'Your safety score needs improvement. Focus on adding emergency contacts and completing form checks.',
        icon: AlertTriangle
      });
    }
    
    return recs;
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 90) return 'bg-green-100';
    if (score >= 70) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Analyzing your safety data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/safety')}
            className="flex items-center text-purple-600 hover:text-purple-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Safety Features
          </button>
          
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-3xl font-bold text-gray-900">Safety Score Analysis</h1>
              <div className={`px-4 py-2 rounded-full ${getScoreBgColor(safetyScore)}`}>
                <span className={`text-2xl font-bold ${getScoreColor(safetyScore)}`}>
                  {safetyScore}
                </span>
                <span className="text-gray-600 ml-2">/ 100</span>
              </div>
            </div>
            
            <p className="text-gray-600 mb-6">
              Your safety score is calculated based on multiple factors including pain management, 
              exercise form, fatigue levels, consistency, and recovery patterns.
            </p>
            
            <div className="flex items-center text-sm text-gray-500">
              <Info className="w-4 h-4 mr-2" />
              Last updated: {new Date().toLocaleDateString()}
            </div>
          </div>
        </div>

        {/* Score Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Base Score</h3>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(scoreBreakdown.baseScore)} ${getScoreBgColor(scoreBreakdown.baseScore)}`}>
                {scoreBreakdown.baseScore}
              </span>
            </div>
            <p className="text-sm text-gray-600">
              Starting safety score baseline for all users.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Emergency Contacts</h3>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(scoreBreakdown.emergencyContacts * 10)} ${getScoreBgColor(scoreBreakdown.emergencyContacts * 10)}`}>
                +{scoreBreakdown.emergencyContacts}
              </span>
            </div>
            <p className="text-sm text-gray-600">
              Bonus points for having emergency contacts ({contacts.length} contacts).
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Form Checks</h3>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(scoreBreakdown.formChecks * 10)} ${getScoreBgColor(scoreBreakdown.formChecks * 10)}`}>
                +{scoreBreakdown.formChecks}
              </span>
            </div>
            <p className="text-sm text-gray-600">
              Bonus points for completing form checks ({formChecks ? Object.values(formChecks).flat().length : 0} checks).
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Fatigue Management</h3>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(scoreBreakdown.fatigueManagement * 20)} ${getScoreBgColor(scoreBreakdown.fatigueManagement * 20)}`}>
                {scoreBreakdown.fatigueManagement > 0 ? '+' : ''}{scoreBreakdown.fatigueManagement.toFixed(1)}
              </span>
            </div>
            <p className="text-sm text-gray-600">
              Adjustment based on your current fatigue level.
            </p>
          </div>
        </div>

        {/* Recommendations */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Personalized Recommendations</h2>
          
          {recommendations.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Excellent Safety Profile!</h3>
              <p className="text-gray-600">
                Your safety score indicates excellent practices. Keep up the great work!
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {recommendations.map((rec, index) => {
                const IconComponent = rec.icon;
                return (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 mr-4">
                        <IconComponent className="w-6 h-6 text-purple-600" />
                      </div>
                      <div className="flex-grow">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{rec.category}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(rec.priority)}`}>
                            {rec.priority.toUpperCase()}
                          </span>
                        </div>
                        <p className="text-gray-600">{rec.message}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions to Improve Your Score</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button 
              onClick={() => navigate('/safety')}
              className="p-4 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors text-left"
            >
              <div className="flex items-center mb-2">
                <Shield className="w-5 h-5 text-purple-600 mr-2" />
                <h4 className="font-medium text-purple-800">Safety Features</h4>
              </div>
              <p className="text-sm text-gray-600">Add emergency contacts and manage safety settings</p>
            </button>
            
            <button 
              onClick={() => navigate('/elite/ai-training')}
              className="p-4 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors text-left"
            >
              <div className="flex items-center mb-2">
                <Activity className="w-5 h-5 text-blue-600 mr-2" />
                <h4 className="font-medium text-blue-800">AI Training</h4>
              </div>
              <p className="text-sm text-gray-600">Access AI-powered form analysis features</p>
            </button>
            
            <button 
              onClick={() => navigate('/safety/fatigue')}
              className="p-4 border border-green-200 rounded-lg hover:bg-green-50 transition-colors text-left"
            >
              <div className="flex items-center mb-2">
                <Heart className="w-5 h-5 text-green-600 mr-2" />
                <h4 className="font-medium text-green-800">Track Fatigue</h4>
              </div>
              <p className="text-sm text-gray-600">Monitor your fatigue levels and recovery</p>
            </button>
          </div>
        </div>

        {/* How to Improve Your Score */}
        <div className="mt-8 bg-green-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-green-900 mb-4">How to Improve Your Safety Score</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-green-800">
            <div>
              <h4 className="font-medium mb-2">📞 Add Emergency Contacts</h4>
              <p>Go to Safety Features → Emergency Contacts and add at least 2 contacts. Each contact adds +5 points to your score.</p>
            </div>
            <div>
              <h4 className="font-medium mb-2">✅ Complete Form Checks (Elite Feature)</h4>
              <p>Use AI-powered form analysis features during workouts to get technique feedback. Each analysis adds +1 point to your score. Requires Elite subscription.</p>
            </div>
            <div>
              <h4 className="font-medium mb-2">😴 Manage Fatigue</h4>
              <p>Track your fatigue levels in Safety Features → Fatigue Tracking. Lower fatigue levels improve your score.</p>
            </div>
            <div>
              <h4 className="font-medium mb-2">📊 Track Progress</h4>
              <p>Use the safety tracking features regularly to maintain and improve your safety score over time.</p>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="mt-8 bg-blue-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-4">How Your Safety Score is Calculated</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
            <div>
              <h4 className="font-medium mb-2">Base Score (75 points)</h4>
              <p>Starting safety score baseline for all users.</p>
            </div>
            <div>
              <h4 className="font-medium mb-2">Emergency Contacts (up to +10 points)</h4>
              <p>Bonus points for having emergency contacts (5 points per contact, max 10).</p>
            </div>
            <div>
              <h4 className="font-medium mb-2">Form Checks (up to +10 points)</h4>
              <p>Bonus points for completing form checks (1 point per check, max 10).</p>
            </div>
            <div>
              <h4 className="font-medium mb-2">Fatigue Management (adjustment)</h4>
              <p>Adjustment based on current fatigue level (lower fatigue = higher score).</p>
            </div>
            <div className="md:col-span-2">
              <h4 className="font-medium mb-2">Total Score</h4>
              <p>Base Score + Emergency Contacts + Form Checks + Fatigue Adjustment (capped at 100)</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SafetyScoreAnalysis;
